'use client';

import { useSearchParams } from 'next/navigation';
import { Suspense, useState } from 'react';

function LoginForm() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const searchParams = useSearchParams();
  const callbackUrl = searchParams.get('callbackUrl') || '/';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSent(false);
    setLoading(true);

    try {
      const res = await fetch('/api/auth/magic/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, callbackUrl }),
      });
      const json = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(json.error || 'Could not send sign-in link.');
      }

      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send sign-in link.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8 relative overflow-hidden" style={{ background: '#0B1120' }}>
      <div className="absolute inset-0 opacity-15">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full blur-3xl" style={{ background: 'radial-gradient(circle, rgba(37,99,235,0.3) 0%, rgba(212,168,67,0.1) 50%, transparent 100%)' }} />
      </div>

      <div className="max-w-md w-full space-y-8 relative z-10">
        <div className="text-center">
          <img
            src="/logo.png"
            alt="24HR Truck Services"
            style={{ width: '180px', height: 'auto', margin: '0 auto 24px auto', display: 'block' }}
          />
          <h2 className="text-xl sm:text-2xl font-semibold text-white flex items-center justify-center gap-2">
            <span>🛠️</span> Agents Command Center
          </h2>
          <p className="mt-2 text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>
            24/7 Marketing Operations • AI-Powered
          </p>
        </div>

        <div className="rounded-2xl p-6 sm:p-8 shadow-2xl" style={{ background: 'linear-gradient(to bottom right, #111827, #0B1120)', border: '1px solid rgba(37,99,235,0.3)' }}>
          {(error || searchParams.get('error')) && (
            <div className="mb-6 px-4 py-3 rounded-lg text-sm" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.5)', color: '#EF4444' }}>
              {error || 'The sign-in link is invalid or expired. Request a new link.'}
            </div>
          )}

          {sent && (
            <div className="mb-6 px-4 py-3 rounded-lg text-sm" style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.45)', color: '#86EFAC' }}>
              Check your email for the sign-in link.
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-2" style={{ color: 'rgba(255,255,255,0.8)' }}>
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="block w-full px-4 py-3 rounded-lg text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent transition-all min-h-[48px]"
                style={{ background: '#0B1120', border: '1px solid rgba(37,99,235,0.2)' }}
                placeholder="name@example.com"
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-3 px-4 rounded-lg text-sm font-semibold text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2563EB] disabled:opacity-50 disabled:cursor-not-allowed transition-all min-h-[48px] active:opacity-80 hover:opacity-90"
              style={{ background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 50%, #D4A843 100%)', boxShadow: '0 4px 20px rgba(37,99,235,0.3)' }}
            >
              {loading ? 'Sending...' : 'Send Magic Link'}
            </button>
          </form>
        </div>

        <div className="text-center text-xs" style={{ color: 'rgba(255,255,255,0.25)' }}>
          <p>🔒 Allowlisted access only</p>
          <p className="mt-1" style={{ color: '#D4A843' }}>24HR Truck Services, Inc.</p>
        </div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B1120' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4A843]" />
      </div>
    }>
      <LoginForm />
    </Suspense>
  );
}
